<script setup lang="ts">
const { totalItems, isEmpty } = useQuoteCart()

// Fetch navigation categories from database
const { data: categories } = useNavigationCategories()

// Group categories: Premium brands vs Main categories
const premiumBrands = computed(() =>
  categories.value?.filter(c => c.is_premium_brand) || []
)
const mainCategories = computed(() =>
  categories.value?.filter(c => !c.is_premium_brand) || []
)

const navigation = [
  { label: 'Contacto', to: '/contacto', icon: 'i-heroicons-phone' },
]

// Mobile menu state
const mobileMenuOpen = ref(false)

// Dropdown state for products menu
const productsDropdownOpen = ref(false)
</script>

<template>
  <header class="sticky top-0 z-50 bg-white dark:bg-neutral-900 shadow-sm">
    <!-- Main header -->
    <UContainer>
      <div class="flex items-center justify-between h-16 md:h-18">
        <!-- Logo -->
        <NuxtLink to="/" class="flex items-center gap-3 group" aria-label="Ir al inicio">
          <img
            src="/logo-acoxpa.png"
            alt="Pinturas Acoxpa - Granados"
            class="h-14 w-auto md:h-16"
            width="180"
            height="64"
            loading="eager"
            fetchpriority="high"
          />
        </NuxtLink>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center gap-1">
          <!-- Products Dropdown -->
          <UPopover
            :open="productsDropdownOpen"
            @update:open="productsDropdownOpen = $event"
          >
            <UButton
              variant="ghost"
              color="neutral"
              class="flex items-center gap-2 px-4 py-2 font-medium"
              trailing-icon="i-heroicons-chevron-down"
            >
              <UIcon name="i-heroicons-squares-2x2" class="w-4 h-4" />
              Productos
            </UButton>

            <template #content>
              <div class="p-4 w-80">
                <!-- All Products Link -->
                <NuxtLink
                  to="/catalogo"
                  class="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors mb-3"
                  @click="productsDropdownOpen = false"
                >
                  <div class="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <UIcon name="i-heroicons-squares-2x2" class="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <p class="font-semibold text-neutral-900 dark:text-white">Ver Todo el Catálogo</p>
                    <p class="text-xs text-neutral-500">Explorar todos los productos</p>
                  </div>
                </NuxtLink>

                <div class="border-t border-neutral-200 dark:border-neutral-700 my-3" />

                <!-- Main Categories -->
                <p v-if="mainCategories.length" class="text-xs font-semibold text-neutral-400 uppercase tracking-wider px-3 mb-2">
                  Categorías
                </p>
                <div class="space-y-1 mb-3">
                  <NuxtLink
                    v-for="cat in mainCategories"
                    :key="cat.slug"
                    :to="`/${cat.slug}`"
                    class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    @click="productsDropdownOpen = false"
                  >
                    <div
                      class="w-2 h-2 rounded-full"
                      :style="{ backgroundColor: cat.brand_color }"
                    />
                    <span class="font-medium text-neutral-700 dark:text-neutral-200">{{ cat.name }}</span>
                  </NuxtLink>
                </div>

                <!-- Premium Brands -->
                <p v-if="premiumBrands.length" class="text-xs font-semibold text-neutral-400 uppercase tracking-wider px-3 mb-2">
                  Marcas Premium
                </p>
                <div class="space-y-1">
                  <NuxtLink
                    v-for="brand in premiumBrands"
                    :key="brand.slug"
                    :to="`/${brand.slug}`"
                    class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    @click="productsDropdownOpen = false"
                  >
                    <div
                      class="w-2 h-2 rounded-full"
                      :style="{ backgroundColor: brand.brand_color }"
                    />
                    <span class="font-medium text-neutral-700 dark:text-neutral-200">{{ brand.name }}</span>
                    <UBadge color="warning" variant="soft" size="xs">Premium</UBadge>
                  </NuxtLink>
                </div>
              </div>
            </template>
          </UPopover>

          <!-- Other Navigation Items -->
          <NuxtLink
            v-for="item in navigation"
            :key="item.to"
            :to="item.to"
            class="flex items-center gap-2 px-4 py-2 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-primary-500 dark:hover:text-primary-400 transition-all font-medium"
            active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-500 dark:text-primary-400"
          >
            <UIcon :name="item.icon" class="w-4 h-4" />
            {{ item.label }}
          </NuxtLink>
        </nav>

        <!-- Actions -->
        <div class="flex items-center gap-2">
          <!-- Quote Cart Button - Enhanced for touch -->
          <UButton
            to="/cotizacion"
            color="primary"
            variant="solid"
            size="lg"
            class="relative touch-target"
            aria-label="Ver cotización"
          >
            <UIcon name="i-heroicons-clipboard-document-list" class="w-5 h-5" />
            <span class="hidden sm:inline ml-2 font-semibold">Cotización</span>
            <ClientOnly>
              <UBadge
                v-if="totalItems > 0"
                :label="String(totalItems)"
                color="error"
                size="sm"
                class="absolute -top-2 -right-2 min-w-[1.5rem] justify-center"
              />
            </ClientOnly>
          </UButton>

          <!-- Mobile menu button -->
          <UButton
            variant="ghost"
            color="neutral"
            class="md:hidden touch-target"
            :icon="mobileMenuOpen ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
            aria-label="Menu"
            size="lg"
            @click="mobileMenuOpen = !mobileMenuOpen"
          />
        </div>
      </div>
    </UContainer>

    <!-- Mobile Navigation Menu -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="mobileMenuOpen"
        class="md:hidden bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 shadow-lg"
      >
        <UContainer class="py-4">
          <nav class="flex flex-col gap-2">
            <!-- All Products -->
            <NuxtLink
              to="/catalogo"
              class="flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors font-medium touch-target"
              active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-500"
              @click="mobileMenuOpen = false"
            >
              <UIcon name="i-heroicons-squares-2x2" class="w-5 h-5" />
              Todos los Productos
            </NuxtLink>

            <!-- Main Categories -->
            <div v-if="mainCategories.length" class="pt-2">
              <p class="px-4 py-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                Categorías
              </p>
              <NuxtLink
                v-for="cat in mainCategories"
                :key="cat.slug"
                :to="`/${cat.slug}`"
                class="flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors font-medium touch-target"
                active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-500"
                @click="mobileMenuOpen = false"
              >
                <div
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: cat.brand_color }"
                />
                {{ cat.name }}
              </NuxtLink>
            </div>

            <!-- Premium Brands -->
            <div v-if="premiumBrands.length" class="pt-2">
              <p class="px-4 py-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                Marcas Premium
              </p>
              <NuxtLink
                v-for="brand in premiumBrands"
                :key="brand.slug"
                :to="`/${brand.slug}`"
                class="flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors font-medium touch-target"
                active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-500"
                @click="mobileMenuOpen = false"
              >
                <div
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: brand.brand_color }"
                />
                {{ brand.name }}
                <UBadge color="warning" variant="soft" size="xs" class="ml-auto">Premium</UBadge>
              </NuxtLink>
            </div>

            <!-- Divider -->
            <div class="border-t border-neutral-200 dark:border-neutral-700 my-2" />

            <!-- Other Navigation Items -->
            <NuxtLink
              v-for="item in navigation"
              :key="item.to"
              :to="item.to"
              class="flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors font-medium touch-target"
              active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-500"
              @click="mobileMenuOpen = false"
            >
              <UIcon :name="item.icon" class="w-5 h-5" />
              {{ item.label }}
            </NuxtLink>
          </nav>

          <!-- Mobile contact info -->
          <div class="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <a
              href="tel:+525582960319"
              class="flex items-center gap-3 px-4 py-3 text-neutral-600 dark:text-neutral-300 hover:text-primary-500 transition-colors"
            >
              <UIcon name="i-heroicons-phone" class="w-5 h-5" />
              <span>+52 55 8296 0319</span>
            </a>
          </div>
        </UContainer>
      </div>
    </Transition>
  </header>
</template>
